import {
	BUYER_ADMIN_ONLY,
	SUPPLIER_ADMIN_ONLY,
	BUYER_ROLES,
	SUPPLIER_ROLES,
	BUYER_ADMIN,
	SUPPLIER_ADMIN,
} from '../components/roles/roles';
import { nullSafeGet, isAdminUser, compareStrings } from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';
import {
	PERMISSION_NAMES,
	getBuyerConfigForPermission,
	getSupplierConfigForPermission,
	getRolePermissionKeyMap,
	isUserAllowedToAccess,
} from './AuthUtils';
import { Document, DocumentPermissions } from '../types/documentTypes';

export const addressValidator = (rule, value, callback, source, options, values: any = {}) => {
	const { streetAddress1, city, region, country } = values;
	if (!value || !streetAddress1 || !city || !region || !country) {
		callback('Please search for and select an valid address');
	} else {
		callback();
	}
};

export const addressValidatorFormv3 = (form) => (rule, value, callback, source, options) => {
	const streetAddress1 = form.getFieldValue('primaryAddress-streetAddress1');
	const city = form.getFieldValue('primaryAddress-city');
	const region = form.getFieldValue('primaryAddress-region');
	const country = form.getFieldValue('primaryAddress-country');
	addressValidator(rule, value, callback, source, options, {
		streetAddress1,
		city,
		region,
		country,
	});
};

export const addressValidatorFormv4 = (getFieldValue) => (_, value) => {
	const streetAddress1 = getFieldValue('primaryAddress-streetAddress1');
	const city = getFieldValue('primaryAddress-city');
	const region = getFieldValue('primaryAddress-region');
	const country = getFieldValue('primaryAddress-country');
	if (value && (!streetAddress1 || !city || !region || !country)) {
		return Promise.reject(new Error('Please search for and select a valid address'));
	} else {
		return Promise.resolve();
	}
};

export const getCompanyWideDocumentPermissions = (userType: string, companyConfig: any) => {
	const getConfigForPermission = (permissionName: string) => {
		return userType === ROLE_TYPES.BUYER
			? getBuyerConfigForPermission(permissionName, companyConfig)
			: getSupplierConfigForPermission(permissionName, companyConfig);
	};

	const accessConfig = getConfigForPermission(PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS) || {};
	const modifyConfig = getConfigForPermission(PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS) || {};

	const accessPermissions: any = {};
	const allKeys = new Set([...Object.keys(accessConfig), ...Object.keys(modifyConfig)]);
	allKeys.forEach((key) => {
		if (accessConfig[key] === true || modifyConfig[key] === true) {
			accessPermissions[key] = true;
		}
	});

	const modifyPermissions: any = {};
	Object.keys(modifyConfig).forEach((key) => {
		if (modifyConfig[key] === true) {
			modifyPermissions[key] = true;
		}
	});

	return {
		access: accessPermissions,
		modify: modifyPermissions,
	};
};

export const mergeDocumentPermissions = (documentPerms: any, companyPerms: any) => {
	if (!documentPerms) {
		return {
			access: { ...(companyPerms.access || {}) },
			modify: { ...(companyPerms.modify || {}) },
		};
	}

	return {
		access: {
			...(companyPerms.access || {}),
			...(documentPerms.access || {}),
		},
		modify: {
			...(companyPerms.modify || {}),
			...(documentPerms.modify || {}),
		},
	};
};

export const isDocumentCreator = (document, currentUser) => {
	const documentCreatedBy = nullSafeGet('createdBy', document);
	const userEmail = nullSafeGet('email', currentUser);
	return compareStrings(documentCreatedBy, userEmail);
};

export const isRoleAllowedForDocumentPermission = (
	permissions: any,
	userType: string,
	role: string,
	permissionType: 'access' | 'modify'
) => {
	if (!permissions) {
		return false;
	}

	const rolePermissionMap = getRolePermissionKeyMap(userType);
	const permissionKey = rolePermissionMap[role];
	if (!permissionKey) {
		return false;
	}

	const permissionConfig = nullSafeGet(permissionType, permissions) || {};
	return permissionConfig[permissionKey] === true;
};

export const canRoleModifyDocumentFromPermissions = (
	permissions: any,
	userType: string,
	role: string
) => {
	return isRoleAllowedForDocumentPermission(permissions, userType, role, 'modify');
};

export const canRoleAccessDocumentFromPermissions = (
	permissions: any,
	userType: string,
	role: string
) => {
	if (!permissions) {
		return false;
	}

	return (
		isRoleAllowedForDocumentPermission(permissions, userType, role, 'access') ||
		isRoleAllowedForDocumentPermission(permissions, userType, role, 'modify')
	);
};

const isRoleExplicitlyDenied = (permissionConfig, userType, roles = []) => {
	if (!permissionConfig) {
		return false;
	}

	const roleName = getRolePermissionKeyMap(userType);

	return roles.some((role) => {
		const permissionKey = roleName[role];
		return (
			permissionKey &&
			Object.prototype.hasOwnProperty.call(permissionConfig, permissionKey) &&
			permissionConfig[permissionKey] === false
		);
	});
};

const checkRoleAccess = (permissionConfig, userType, roles = []) => {
	if (!permissionConfig) {
		return false;
	}

	if (roles.some((role) => BUYER_ADMIN_ONLY.has(role) || SUPPLIER_ADMIN_ONLY.has(role))) {
		return true;
	}

	const roleName = getRolePermissionKeyMap(userType);
	return roles.map((_) => nullSafeGet(`${roleName[_]}`, permissionConfig)).some((_) => !!_);
};

const canUserAccessDocument = (
	document,
	currentUser,
	companySettings,
	permissionType: 'access' | 'modify',
	fallbackPermissionNames: string[]
) => {
	const userType = nullSafeGet('userType', currentUser);
	const roles = nullSafeGet('roles', currentUser) || [];

	if (isDocumentCreator(document, currentUser)) {
		return true;
	}

	let permissions;
	if (userType === ROLE_TYPES.BUYER) {
		permissions = nullSafeGet('buyerPermissions', document);
	} else if (userType === ROLE_TYPES.SUPPLIER) {
		permissions = nullSafeGet('supplierPermissions', document);
	}

	const permissionConfig = permissions && nullSafeGet(permissionType, permissions);

	if (permissionConfig) {
		if (isRoleExplicitlyDenied(permissionConfig, userType, roles)) {
			return false;
		}

		if (checkRoleAccess(permissionConfig, userType, roles)) {
			return true;
		}
	}

	return fallbackPermissionNames.some((permissionName) =>
		isUserAllowedToAccess(userType)(permissionName, companySettings, roles)
	);
};

export const canUserViewDocument = (document, currentUser, companySettings) => {
	return canUserAccessDocument(document, currentUser, companySettings, 'access', [
		PERMISSION_NAMES.ACCESS_LOCATION_DOCUMENTS,
		PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
	]);
};

export const canUserEditDocument = (document, currentUser, companySettings) => {
	return canUserAccessDocument(document, currentUser, companySettings, 'modify', [
		PERMISSION_NAMES.MODIFY_LOCATION_DOCUMENTS,
	]);
};

export const canUserDeleteDocument = (document, currentUser) => {
	return isDocumentCreator(document, currentUser) || isAdminUser(currentUser);
};

export const getDocumentPermissionsByUserType = (document, userType: string) => {
	if (userType === ROLE_TYPES.BUYER) {
		return nullSafeGet('buyerPermissions', document);
	}
	return nullSafeGet('supplierPermissions', document);
};

export const getMergedDocumentPermissions = (document, userType: string, companyConfig: any) => {
	const companyWidePermissions = getCompanyWideDocumentPermissions(userType, companyConfig);
	const documentPermissions = getDocumentPermissionsByUserType(document, userType);
	return mergeDocumentPermissions(documentPermissions, companyWidePermissions);
};

export const normalizePermissionsForAllRoles = (userType: string, permissionsToNormalize) => {
	const rolePermissionMap = getRolePermissionKeyMap(userType);
	const allRoles = userType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ROLES : BUYER_ROLES;
	const availableRoles = allRoles.filter((role) => role !== BUYER_ADMIN && role !== SUPPLIER_ADMIN);

	const currentAccess = nullSafeGet('access', permissionsToNormalize) || {};
	const currentModify = nullSafeGet('modify', permissionsToNormalize) || {};

	const normalizedPermissions = {
		access: {},
		modify: {},
	};

	availableRoles.forEach((role) => {
		const permissionKey = rolePermissionMap[role];
		if (!permissionKey) {
			return;
		}
		normalizedPermissions.access[permissionKey] = !!currentAccess[permissionKey];
		normalizedPermissions.modify[permissionKey] = !!currentModify[permissionKey];
	});

	return normalizedPermissions;
};
